import { de } from '../../i18n/de';
import { useProfileStore } from '../../core/profile/ProfileStore';
import { enterLowEffort, exitLowEffort } from '../../core/profile/lowEffort';
import { keyboardBandHeight } from '../../core/profile/cssVars';
import { emit } from '../../core/events';
import { availableLayouts } from '../../core/profile/layouts';
import { KEY_SIZE_CEILING_MM } from '../../core/calibration/inference';
import { prerenderMissing } from '../../core/speech/prerender';
import { speechService } from '../../core/speech/SpeechService';
import { isValidNewPin, type CareAction } from '../../core/profile/pinGate';
import { CareButton } from './BetreuerModus';
import type { ThemeId } from '../../core/types';
import type { InputModeId } from '../../core/input/InputAdapter';

/**
 * Every field in `Settings` a caregiver may need, and nothing hard-coded elsewhere.
 *
 * Two things here are decisions rather than plumbing:
 *
 *  1. **The input-mode picker only offers modes that exist.** Selecting a mode with no adapter would
 *     leave the person unable to activate anything at all — with care mode, the only way to undo it,
 *     reachable only by a long-press that thankfully does not go through the adapter. So a mode with no
 *     adapter is shown disabled rather than left to fail on the person.
 *
 *     The boundary has moved as adapters landed and only `hardwareKeyboard` is left behind it: dwell and
 *     scanning gained adapters in Phase 3, and `partnerAssisted` is **offered** even though `adapterFor`
 *     resolves it to direct touch, because that fallback is its design rather than a gap (`CFG-03`,
 *     `INP-13`). What `hardwareKeyboard` lacks is a mode of its own — a Bluetooth switch presents as a
 *     keyboard and is served by scanning's Space/Enter path.
 *  2. **Key size says when it cannot help.** Above ~19.5 mm the frame cannot grow a key, so the
 *     slider would move and nothing would change. The panel says so and points at the 10-column
 *     layout, which is what "bigger" actually means here.
 */

/** Modes with a working adapter today. */
const IMPLEMENTED_MODES: InputModeId[] = ['directTouch', 'dwell', 'scanning', 'partnerAssisted'];

const MODE_LABELS: Record<InputModeId, string> = {
  directTouch: 'Direktes Antippen',
  dwell: 'Verweilen',
  scanning: 'Scannen',
  partnerAssisted: 'Buchstaben abfragen',
  hardwareKeyboard: 'Externe Tastatur',
};

const THEME_LABELS: Record<ThemeId, string> = {
  blackOnWhite: 'Schwarz auf Weiss',
  whiteOnBlack: 'Weiss auf Schwarz',
  yellowOnBlack: 'Gelb auf Schwarz',
  highContrastBlue: 'Blau, hoher Kontrast',
};

export function SettingsPanel({
  guard,
}: {
  guard: (action: CareAction, run: () => void) => void;
}): React.JSX.Element {
  const settings = useProfileStore((s) => s.settings);
  const update = useProfileStore((s) => s.update);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 'var(--font-body)' }}>
      <Row label="Eingabe">
        {(Object.keys(MODE_LABELS) as InputModeId[]).map((mode) => {
          const available = IMPLEMENTED_MODES.includes(mode);
          return (
            <CareButton
              key={mode}
              id={`care-mode:${mode}`}
              label={MODE_LABELS[mode]}
              active={settings.inputMode === mode}
              disabled={!available}
              {...(available ? {} : { note: de.inputModeUnavailable })}
              onPress={() => void update({ inputMode: mode })}
            />
          );
        })}
      </Row>

      <Row label="Darstellung">
        {(Object.keys(THEME_LABELS) as ThemeId[]).map((theme) => (
          <CareButton
            key={theme}
            id={`care-theme:${theme}`}
            label={THEME_LABELS[theme]}
            active={settings.theme === theme}
            onPress={() => void update({ theme })}
          />
        ))}
      </Row>

      <Row label="Tastatur">
        {availableLayouts().map((layout) => (
          <CareButton
            key={layout.id}
            id={`care-layout:${layout.id}`}
            label={layout.name}
            active={settings.keyboardLayout === layout.id}
            onPress={() => void update({ keyboardLayout: layout.id as typeof settings.keyboardLayout })}
          />
        ))}
      </Row>

      {/* Dwell time (3.1), and it only appears in dwell mode — a slider that does nothing is worse than an
          absent one, which is the same reasoning that disables the unimplemented input modes above. The
          adapter reads this live, so a caregiver moving it sees the very next press respond. */}
      {settings.inputMode === 'dwell' ? (
        <Slider
          id="care-dwellMs"
          label={`Haltezeit ${settings.dwellMs} ms`}
          min={400}
          max={2000}
          step={100}
          value={settings.dwellMs}
          onChange={(dwellMs) => void update({ dwellMs })}
        />
      ) : null}

      {/* Scanning's two timings, mode-gated for the same reason as the dwell slider. Both are read live on
          every tick, so moving either changes the very next step. */}
      {settings.inputMode === 'scanning' ? (
        <>
          <Slider
            id="care-scanIntervalMs"
            label={`Schritt-Tempo ${settings.scanIntervalMs} ms`}
            min={500}
            max={3000}
            step={100}
            value={settings.scanIntervalMs}
            onChange={(scanIntervalMs) => void update({ scanIntervalMs })}
          />
          <Slider
            id="care-scanLoops"
            label={`Durchgänge ${settings.scanLoops}`}
            min={1}
            max={5}
            step={1}
            value={settings.scanLoops}
            onChange={(scanLoops) => void update({ scanLoops })}
          />
        </>
      ) : null}

      <Slider
        id="care-keyMm"
        label={`Tastengrösse ${settings.keyMm} mm`}
        min={16}
        max={24}
        step={0.5}
        value={settings.keyMm}
        onChange={(keyMm) => void update({ keyMm })}
      />
      {settings.keyMm >= KEY_SIZE_CEILING_MM ? (
        <p data-testid="care-key-ceiling" style={{ opacity: 0.85 }}>
          Grösser als {KEY_SIZE_CEILING_MM} mm kann die Tastatur nicht anzeigen. Für grössere Ziele
          das Layout mit weiten Abständen wählen — es hat aber kein Komma, keinen Punkt und keinen
          Bindestrich.
        </p>
      ) : null}

      <Slider
        id="care-band"
        label={`Inhaltsband ${settings.contentBandPct} %`}
        min={40}
        max={100}
        step={5}
        value={settings.contentBandPct}
        onChange={(contentBandPct) => void update({ contentBandPct })}
      />
      <Slider
        id="care-font"
        label={`Schrift ${settings.fontScale.toFixed(1)}×`}
        min={1}
        max={2.5}
        step={0.1}
        value={settings.fontScale}
        onChange={(fontScale) => void update({ fontScale })}
      />
      <Slider
        id="care-repeat"
        label={`Zittern-Sperre ${settings.repeatSuppressMs} ms`}
        min={300}
        max={600}
        step={50}
        value={settings.repeatSuppressMs}
        onChange={(repeatSuppressMs) => void update({ repeatSuppressMs })}
      />
      <Slider
        id="care-key-repeat"
        label={`Tasten-Sperre ${settings.keyRepeatSuppressMs} ms`}
        min={0}
        max={300}
        step={20}
        value={settings.keyRepeatSuppressMs}
        onChange={(keyRepeatSuppressMs) => void update({ keyRepeatSuppressMs })}
      />
      <p style={{ opacity: 0.85 }}>
        Die Tasten-Sperre muss klein bleiben: sonst verschluckt sie den zweiten Buchstaben von
        Wörtern wie «alle» oder «Kaffee».
      </p>

      <Row label="Stimme">
        {['de-CH-LeniNeural', 'de-CH-JanNeural', 'de-DE-KatjaNeural'].map((voiceId) => (
          <CareButton
            key={voiceId}
            id={`care-voice:${voiceId}`}
            label={voiceId.replace('Neural', '')}
            active={settings.voiceId === voiceId}
            onPress={() => void update({ voiceId })}
          />
        ))}
      </Row>

      {/* Key echo is cache-only by design — a letter fetched over the network arrives after the next
          keypress, and a late echo is worse than none. So switching it on renders the letters now,
          while a caregiver is here and probably online, rather than leaving them to find
          «Audio vorbereiten» and discover the silence in a conversation. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-key-echo"
          type="checkbox"
          checked={settings.keyEchoEnabled}
          onChange={(event) => {
            const keyEchoEnabled = event.target.checked;
            void update({ keyEchoEnabled }).then(() => {
              if (!keyEchoEnabled) return;
              const provider = speechService.chain().find((entry) => entry.id === 'azureTts');
              if (provider) void prerenderMissing({ provider, voiceId: settings.voiceId });
            });
          }}
        />
        Tasten laut mitsprechen
      </label>
      {settings.keyEchoEnabled ? (
        <>
          {/* The only lever between "the person cannot hear the letter" and "the room hears every
              letter" — there is no arrangement in between, because Safari implements `setSinkId`
              nowhere. See docs/private-output.md. */}
          <Slider
            id="care-key-echo-volume"
            label={`Lautstärke Tasten ${settings.keyEchoVolume.toFixed(1)}×`}
            min={0.1}
            max={1}
            step={0.1}
            value={settings.keyEchoVolume}
            onChange={(keyEchoVolume) => void update({ keyEchoVolume })}
          />
        </>
      ) : null}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-confirm-tap"
          type="checkbox"
          checked={settings.confirmOnPhraseTap}
          onChange={(event) => void update({ confirmOnPhraseTap: event.target.checked })}
        />
        Sätze erst nach Rückfrage sprechen
      </label>
      {/* **The only way in and out** since ADR-039 (3.7, `SUR-13`). This was the second route while the person
          had a `Leichter` pill in the contextual band; that pill's slot is the `123`/`ABC` switch now, because a
          permanent control needs it permanently and the band has no sixth flex unit to give.
          What is traded away is 3.7's own reason for one-tap access — "fatigue is when the person least wants to
          navigate" — and that is a real cost on a bad day, not a tidy-up. It is the first question in
          `docs/device-checklist-phase-5.md`, and it is the answer that would reverse the decision. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-low-effort"
          type="checkbox"
          checked={settings.lowEffortMode}
          onChange={() => {
            const viewport = {
              widthPx: typeof window === 'undefined' ? 1180 : window.innerWidth,
              keyboardHeightPx: keyboardBandHeight(),
            };
            void update(
              settings.lowEffortMode
                ? exitLowEffort(settings, viewport)
                : enterLowEffort(settings, viewport),
            );
            emit({ type: 'LowEffortToggled', on: !settings.lowEffortMode });
          }}
        />
        {de.careLowEffort}
      </label>

      {/* 3.9, off by default. It takes the keyboard away right after a sentence, which is helpful when the
          partner needs to read along and unhelpful when the person was about to type another one — so it is the
          family's choice rather than ours. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-auto-partner"
          type="checkbox"
          checked={settings.autoPartnerAfterSpeak}
          onChange={(event) => void update({ autoPartnerAfterSpeak: event.target.checked })}
        />
        Nach dem Sprechen Partner-Ansicht zeigen
      </label>

      {/* A tester spoke one word to hear it, found the word gone, and had to type it again to build a
          sentence. Off by default all the same: somebody who speaks whole sentences would find an
          output bar that never empties itself worse than one that does. There is still no clear-all
          button; with this on, the sentence is abandoned by holding the output bar for two seconds
          (`SUR-26`), which is undoable and nowhere near `Wort löschen`. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-keep-text"
          type="checkbox"
          checked={settings.keepTextAfterSpeak}
          onChange={(event) => void update({ keepTextAfterSpeak: event.target.checked })}
        />
        Wort nach dem Sprechen stehen lassen
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-abbrev-enabled"
          type="checkbox"
          checked={settings.abbrevEnabled}
          onChange={(event) => void update({ abbrevEnabled: event.target.checked })}
        />
        Abkürzungen verwenden
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-prediction-enabled"
          type="checkbox"
          checked={settings.predictionEnabled}
          onChange={(event) => void update({ predictionEnabled: event.target.checked })}
        />
        Wortvorschläge anzeigen
      </label>
      <p style={{ opacity: 0.85 }}>
        Vorschläge lernen erst mit der Zeit die eigenen Wörter. Ob sie das Schreiben wirklich
        schneller machen, wird gemessen — falls nicht, gehören sie ausgeschaltet.
      </p>
      <Slider
        id="care-candidate-count"
        label={`Anzahl Vorschläge ${settings.candidateCount}`}
        min={3}
        max={6}
        step={1}
        value={settings.candidateCount}
        onChange={(candidateCount) => void update({ candidateCount })}
      />

      <Row label={`PIN (${settings.pin === null ? de.noPin : '••••'})`}>
        <CareButton
          id="care:set-pin"
          label={settings.pin === null ? de.carePinSet : de.carePinChange}
          onPress={() => {
            const entered = window.prompt(de.carePinSet);
            if (entered === null) return;
            if (!isValidNewPin(entered)) return;
            void update({ pin: entered });
          }}
        />
        {settings.pin === null ? null : (
          <CareButton
            id="care:clear-pin"
            label="PIN entfernen"
            danger
            onPress={() => guard('resetSettings', () => void update({ pin: null }))}
          />
        )}
      </Row>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div>
      <p style={{ fontWeight: 700, marginBottom: 4 }}>{label}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{children}</div>
    </div>
  );
}

/**
 * Every timing and geometry value is adjustable **while the person is using it** — a caregiver
 * tuning key size should see the effect immediately, not after a reload. `ProfileStore` applies the
 * CSS variables before it persists, so this is live by construction.
 */
function Slider({
  id,
  label,
  min,
  max,
  step,
  value,
  onChange,
}: {
  id: string;
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span>{label}</span>
      <input
        data-testid={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        style={{ minHeight: 'var(--min-target)' }}
      />
    </label>
  );
}
